import { useState } from 'react';
import { Button } from 'reactstrap';
import classNames from 'classnames';
import { TakenFromText } from '@routes/custom-dashboard/items/charts/common/TakenFromText';
import { useContentOverflow } from '@hooks/useContentOverflow';
import { FullTextModal } from './FullTextModal';
import './styles.scss';

interface MetricWidgetDisplayProps {
  text?: string;
  effectiveDate?: string;
  period?: string;
  aggregated?: boolean;
  title?: string;
  onViewSource?: () => void;
}

export const MetricWidgetDisplay = ({
  text,
  effectiveDate,
  period,
  aggregated,
  title,
  onViewSource,
}: MetricWidgetDisplayProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { containerRef, isOverflowing } = useContentOverflow();
  const viewMoreHeight = 48;

  const toggleModal = () => {
    setIsModalOpen((prev) => !prev);
  };

  const handleViewSource = () => {
    toggleModal();
    onViewSource?.();
  };

  const footerHeight = effectiveDate ? 24 : 0;

  return (
    <>
      <div className='w-100 h-100 position-relative'>
        {/* Use position-absolute to force a constrained height for overflow detection.
            Flexbox with h-100 doesn't constrain child height properly in nested flex contexts,
            so we use absolute positioning with top/bottom values to create a fixed-height container.
            This allows useContentOverflow to correctly detect when scrollHeight > clientHeight. */}
        <div
          ref={containerRef}
          className={classNames('expandable-text-widget position-absolute overflow-hidden', {
            'd-flex align-items-center': !isOverflowing,
          })}
          style={{
            top: 0,
            left: 0,
            right: 0,
            bottom: footerHeight + (isOverflowing ? viewMoreHeight : 0),
          }}
        >
          <div
            className={classNames('text-ThemeTextMedium text-break', {
              'is-truncated h-100': isOverflowing,
            })}
          >
            {text}
          </div>
        </div>
        {isOverflowing && (
          <div
            className='position-absolute d-flex justify-content-center w-100'
            style={{ bottom: footerHeight, height: viewMoreHeight }}
          >
            <Button color='link' onClick={toggleModal} size='sm'>
              <div className='text-sm py-1'>View more</div>
            </Button>
          </div>
        )}
        {effectiveDate && (
          <div className='position-absolute w-100' style={{ bottom: 0 }}>
            <TakenFromText effectiveDate={effectiveDate} period={period} aggregated={aggregated} />
          </div>
        )}
      </div>
      <FullTextModal
        isOpen={isModalOpen}
        toggle={toggleModal}
        text={text || ''}
        title={title || 'Metric Text'}
        onViewSource={onViewSource ? handleViewSource : undefined}
      />
    </>
  );
};
